/* eslint-disable no-unused-vars */
/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { cleanUpKey } from 'utils/common';
import { Tabs, Tab, TabPanels, TabPanel, TabList } from '@carbon/react';
import { getTranslatedExtensions } from 'utils/translate';
import Markdown from './Markdown';
import ExtensionsDisplay from './ExtensionsDisplay';
import { handleKeyDown } from 'utils/common';
import SecurityDisplayFlow from './SecurityDisplayFlow';
import { useApi } from 'contexts/ApiHandler';

const SecurityElement = ({ t, classNames, elementKey, elementValue }) => {
  return (
    <div className={`${classNames} keyValueDisplay`}>
      <div className="key">{t(elementKey)}</div>
      <div className="value">{elementValue}</div>
    </div>
  );
};

const getBearerFormat = (securityDefinition) => {
  return securityDefinition.scheme && securityDefinition.scheme === 'bearer' && securityDefinition.bearerFormat ? securityDefinition.bearerFormat : null;
};

const getType = (bearerFormat, securityDefinition) => {
  return bearerFormat ? securityDefinition.type + ' (' + securityDefinition.scheme + ')' : securityDefinition.type;
};

const SecurityDisplay = ({ markupType, security, securityDefinitions, mtls, onChange = () => {}, className, showMultipleSecurityOptionsWarning = false }) => {
  const { t } = useTranslation();
  const { apiHandler } = useApi();

  const multiSecurity = security.length > 1;

  const handleClick = useCallback(
    (index) => {
      let fakeEvent = { target: { id: 'SecurityOption' } };
      onChange(fakeEvent, index);
    },
    [onChange]
  );

  const TabPanelDiv = multiSecurity ? TabPanel : "div"; 

  return (
    <div className={classNames('operationInformation', 'operationSecurity', className)} data-testid={'operation-security'}>
      <div role="heading" aria-level={3} className="labelText">{t('security')}</div>
      <div className={classNames('valueText', 'securityContainer', { hasSingleSecurity: !multiSecurity })}>
        {showMultipleSecurityOptionsWarning && multiSecurity && <div className="multipleOptionsLabel">{t('explorer_security_requirements')}</div>}
        <Tabs className={classNames({ notRendered: !multiSecurity })} aria-label={t('security_options')}>
          {security.length > 1 && (
            <TabList aria-label="security-options-tabs">
              {security.map((securityOptions, index) => {
                return (
                  <Tab
                    key={`tab_${index}`}
                    title={t('security_option_label', { number: index + 1 })}
                    onClick={() => handleClick(index)}
                    onKeyDown={handleKeyDown(() => handleClick(index))}
                    data-testid={`security-tab-${index}`}
                    id={`security-tab-${index}`}
                  >
                    {t('security_option_label', { number: index + 1 })}
                  </Tab>
                );
              })}
            </TabList>
          )}
          <TabPanels>
            {security.map((securityOptions, index) => {
              return (
                <TabPanelDiv key={`securityOption_${index}`} >
                  <div className={classNames('securityOption', `securityOption-${index + 1}`)}>
                    {mtls && mtls.certificate && (
                      <div className="mutualTLS keyValueDisplay" data-testid="mutualTLS">
                        <div className="key securityTitle">{t('mutual_tls')}</div>
                        <div className="value">{t('enabled')}</div>
                      </div>
                    )}
                    {Object.entries(securityOptions).map(([securityKey, securityValue], subIndex) => {
                      // if we have a security object with no matching entry in securityDefinition, we can't do anything sensible here
                      if (!securityDefinitions[securityKey]) {
                        return null;
                      }

                      const securityDefinition = securityDefinitions[securityKey];
                      let bearerFormat = getBearerFormat(securityDefinition);
                      let type = getType(bearerFormat, securityDefinition);
                      let scheme = securityDefinition.scheme ?? null;
                      let customExtensions = apiHandler.getCustomExtensions(securityDefinition);
                      let keyType = null;

                      if (customExtensions) {
                        Object.keys(customExtensions).filter(function (key) {
                          if (key === 'x-key-type') {
                            keyType = customExtensions[key];
                            delete customExtensions[key];
                            if (keyType === 'clientId' || keyType === 'client_id') {
                              keyType = t('client_id');
                            } else if (keyType === 'clientSecret' || keyType === 'client_secret') {
                              keyType = t('client_secret');
                            }
                            return true;
                          }
                          return false;
                        });
                      }

                      return (
                        <div key={subIndex} className={classNames('securityOptionPart', `securityOptionPart-${cleanUpKey(securityKey)}`)}>
                          <div className="securityTitle keyValueDisplay">
                            <div className="key securityTitle">{securityKey}</div>
                            {keyType && (
                              <div className="key securityTitle" data-testid={'security-keytype'}>
                                ({t('type')}: {keyType})
                              </div>
                            )}
                          </div>
                          <div className="securityNameAndType keyValueDisplay">
                            {securityDefinition.name && securityDefinition.name.length !== 0 && (
                              <div className="key securityName">{securityDefinition.name}</div>
                            )}
                            {securityDefinition.in && (
                              <div className="value securityType">{t('api_located_in', { type: securityDefinition.type, in: securityDefinition.in })}</div>
                            )}
                          </div>
                          {bearerFormat && <SecurityElement t={t} classNames="securityOidConnectUrl" elementKey="bearer_format" elementValue={bearerFormat} />}
                          {!securityDefinition.in && (
                            <div className={classNames('securityOidConnectUrl', 'keyValueDisplay')}>
                              <div className="key">
                                {t('type')} {keyType}
                              </div>
                              <div className={classNames('value', 'securityType', { securityName: !securityDefinition.name })}>{type}</div>
                            </div>
                          )}
                          {scheme && <SecurityElement t={t} classNames="securityOidConnectUrl" elementKey="scheme" elementValue={scheme} />}
                          {securityDefinition.openIdConnectUrl && (
                            <SecurityElement
                              t={t}
                              classNames="securityOidConnectUrl"
                              elementKey="explorer_openIdConnect_url"
                              elementValue={securityDefinition.openIdConnectUrl}
                            />
                          )}

                          {securityDefinition.flows &&
                            Object.keys(securityDefinition.flows).map((flow) => {
                              if (!flow || Object.keys(flow).length === 0) {
                                return null;
                              } else {
                                return (
                                  <SecurityDisplayFlow
                                    key={flow + 'flow'}
                                    flowType={flow}
                                    flow={securityDefinition.flows[flow]}
                                    apiHandler={apiHandler}
                                    securityValue={securityValue}
                                  />
                                );
                              }
                            })}
                          {securityDefinition.flow && (
                            <SecurityElement
                              t={t}
                              classNames="securityFlow keyValueDisplay"
                              elementKey="explorer_security_flow"
                              elementValue={securityDefinition.flow}
                            ></SecurityElement>
                          )}
                          {securityDefinition.authorizationUrl && securityDefinition.authorizationUrl.length > 0 && (
                            <div className="securityAuthUrl keyValueDisplay">
                              <div className="key">{t('explorer_authorization_url')}</div>
                              <div className="value">
                                {Array.isArray(securityDefinition.authorizationUrl) && (
                                  <div data-testid="authUrls">
                                    {securityDefinition.authorizationUrl.map((url) => {
                                      return <div key={url} className="url">{url}</div>;
                                    })}
                                  </div>
                                )}
                                {!Array.isArray(securityDefinition.authorizationUrl) && <div>{securityDefinition.authorizationUrl}</div>}
                              </div>
                            </div>
                          )}
                          {securityDefinition.tokenUrl && securityDefinition.tokenUrl.length > 0 && (
                            <div className="securityTokenUrl keyValueDisplay">
                              <div className="key">{t('explorer_token_url')}</div>
                              <div className="value">
                                {Array.isArray(securityDefinition.tokenUrl) && (
                                  <div data-testid="tokenUrls">
                                    {securityDefinition.tokenUrl.map((url) => {
                                      return <div key={url} className="url">{url}</div>;
                                    })}
                                  </div>
                                )}
                                {!Array.isArray(securityDefinition.tokenUrl) && <div>{securityDefinition.tokenUrl}</div>}
                              </div>
                            </div>
                          )}
                          {securityDefinition.scopes && Object.keys(securityDefinition.scopes).length > 0 && securityValue && securityValue.length > 0 && (
                            <div className="securityScopes keyValueDisplay">
                              <div className="key">{t('explorer_scopes')}</div>
                              <div className="value">
                                {Object.keys(securityDefinition.scopes).map(function (scope) {
                                  // Strip off extensions
                                  if (scope.indexOf('$$') > -1 || scope.indexOf('x-') > -1 || securityValue.indexOf(scope) === -1) {
                                    return null;
                                  }

                                  let scopeElement = (
                                    <div key={scope} className="scope">
                                      {scope}
                                    </div>
                                  );
                                  if (securityDefinition.scopes[scope]) {
                                    scopeElement = (
                                      <div key={scope} className="scope">
                                        {`${scope} - ${getTranslatedExtensions(securityDefinition.scopes, scope)}`}
                                      </div>
                                    );
                                  }

                                  return scopeElement;
                                })}
                              </div>
                            </div>
                          )}
                          {securityDefinition.description && (
                            <Markdown className="secondaryText" data={getTranslatedExtensions(securityDefinition, 'description')} markupType={markupType} />
                          )}
                          {customExtensions && <ExtensionsDisplay label="" className="securityExtension" extensions={customExtensions} />}
                        </div>
                      );
                    })}
                  </div>
                </TabPanelDiv>
              );
            })}
          </TabPanels>
        </Tabs>
      </div>
    </div>
  );
};

SecurityDisplay.propTypes = {
  api: PropTypes.string, // The Api document in JSON.
  security: PropTypes.array, // An array of the security options for the Api document.
  securityDefinitions: PropTypes.object, // The security definitions within the Api document.
  markupType: PropTypes.string, // Markup format
  mtls: PropTypes.object, // The Mutual TLS Certificate
  onChange: PropTypes.func, // The onChange function that should be triggered when selecting a new security option tab.
  className: PropTypes.string,
  showMultipleSecurityOptionsWarning: PropTypes.bool // Whether to show a warning about there being multiple security options
};

export default SecurityDisplay;
